import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip as MuiTooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faToggleOn,
  faToggleOff,
  faSpinner,
  faUsers,
  faXmark,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { authFetch } from '../utils/api';
import { canWrite } from '../utils/permissions';
import { useLiveNotifications } from './LiveNotificationsProvider';
import WarningModal from './WarningModal.js';
import InfoTooltip from './InfoTooltip';
import '../pages/bots.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Self-contained agent-availability control for a single bot: the
// online/offline toggle + a team chip that opens a roster modal. Mirrors
// the behaviour that lives inline in the bots overview card so it can be
// reused (e.g. on the new Overview page) without duplicating the flow.
// `onState` ({ roster, online, ready }) tells a caller who is online, e.g. Mo's welcome in the
// AI Studio, which names them next to the controls.
// the last known status and team per agent (page session): shown at once when the component comes back
const lastKnown = new Map();

const AgentAvailability = forwardRef(function AgentAvailability({ bot, onState = null }, ref) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAgentOnline: registerHeartbeat } = useLiveNotifications();

  const botId = bot?.id;
  const known = lastKnown.get(botId);
  const [online, setOnline] = useState(known ? known.online : false);
  const [statusLoading, setStatusLoading] = useState(!known);
  const [toggling, setToggling] = useState(false);
  const [roster, setRoster] = useState(known ? known.roster : null);
  const [rosterReady, setRosterReady] = useState(!!known);
  const loaders = useRef({});
  const [modalOpen, setModalOpen] = useState(false);
  const [forcing, setForcing] = useState({});
  const [pendingForce, setPendingForce] = useState(null);

  // Let a parent (e.g. the Overview live-chat card) open the roster modal.
  useImperativeHandle(ref, () => ({
    openRoster: () => setModalOpen(true),
    refresh: () => Promise.all([loaders.current.status?.(), loaders.current.roster?.()]),
  }), []);

  // Initial online status (one call returns all bots; pick ours).
  useEffect(() => {
    if (!botId) return;
    let alive = true;
    const loadStatus = async () => {
      try {
        const res = await authFetch(`${API}live/agent_status`);
        if (res && res.ok) {
          const data = await res.json();
          const statuses = data.statuses || {};
          if (alive) setOnline(!!statuses[botId]);
          if (statuses[botId]) registerHeartbeat(botId, true);
        }
      } catch (_) {
        /* ignore */
      } finally {
        if (alive) setStatusLoading(false);
      }
    };
    loaders.current.status = loadStatus;
    loadStatus();
    return () => {
      alive = false;
    };
  }, [botId, registerHeartbeat]);

  // Team roster + 30s refresh.
  useEffect(() => {
    if (!botId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await authFetch(`${API}live/agents/${botId}`);
        if (!res || !res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const agents = data.agents || [];
        setRoster(agents);
        const self = agents.find((agent) => agent.is_self);
        if (self) {
          setOnline(self.online);
          registerHeartbeat(botId, self.online);
        }
        setRosterReady(true);
      } catch (_) {
        /* swallow */
      }
    };
    loaders.current.roster = load;
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [botId, registerHeartbeat]);

  const handleToggle = async () => {
    if (!botId) return;
    const newStatus = online ? 'offline' : 'online';
    setToggling(true);
    try {
      const res = await authFetch(`${API}live/agent_status/${botId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res && res.ok) {
        setOnline(newStatus === 'online');
        setRoster((list) =>
          list
            ? list.map((a) => (a.is_self ? { ...a, online: newStatus === 'online' } : a))
            : list
        );
        registerHeartbeat(botId, newStatus === 'online');
        // Let an open test-chat playground re-check availability now so the
        // "Talk to a human" button appears/disappears immediately.
        window.dispatchEvent(new CustomEvent('ub:agent-availability-changed', {
          detail: { botId, online: newStatus === 'online' },
        }));
      }
    } catch (err) {
      console.error('Error toggling agent status:', err);
    }
    setToggling(false);
  };

  const handleForceOffline = async (userId) => {
    if (!canWrite() || !botId) return;
    setForcing((p) => ({ ...p, [userId]: true }));
    try {
      const res = await authFetch(`${API}live/agent_status/${botId}/user/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'offline' }),
      });
      if (res && res.ok) {
        setRoster((list) =>
          list ? list.map((a) => (a.user_id === userId ? { ...a, online: false } : a)) : list
        );
      }
    } catch (_) {
      /* swallow */
    }
    setForcing((p) => {
      const next = { ...p };
      delete next[userId];
      return next;
    });
  };

  const totalAgents = roster ? roster.length : 0;
  const onlineCount = roster ? roster.filter((a) => a.online).length : 0;
  const showTeamChip = !!roster && totalAgents >= 1;
  const isInitialLoading = statusLoading || !rosterReady;
  useEffect(() => {
    if (botId && !isInitialLoading) lastKnown.set(botId, { online, roster });
  }, [botId, online, roster, isInitialLoading]);

  const stateRef = useRef(onState);
  stateRef.current = onState;
  useEffect(() => {
    stateRef.current?.({ roster: roster || [], online, ready: !isInitialLoading });
  }, [roster, online, isInitialLoading]);

  const renderRow = (a) => {
    const displayName =
      a.first_name || a.last_name
        ? `${a.first_name || ''} ${a.last_name || ''}`.trim()
        : a.email || `User #${a.user_id}`;
    const isSelf = a.is_self;
    const canForceOff = !isSelf && a.online && canWrite();
    const interactive = isSelf || canForceOff;
    const busy = isSelf ? toggling : !!forcing[a.user_id];
    const onClick = () => {
      if (!interactive || busy) return;
      if (isSelf) handleToggle();
      else setPendingForce({ userId: a.user_id, name: displayName });
    };
    return (
      <li key={a.user_id} className="live-inbox-agent-row">
        <span className={`live-inbox-agent-dot ${a.online ? 'online' : 'offline'}`} />
        <span className="live-inbox-agent-name" title={a.email || ''}>
          {displayName}
        </span>
        <MuiTooltip
          title={
            interactive
              ? a.online
                ? t('live_inbox_team_set_offline') || 'Set offline'
                : t('live_inbox_team_set_online') || 'Set online'
              : isSelf
              ? ''
              : t('live_inbox_team_toggle_disabled') ||
                'Only this teammate can set themselves online.'
          }
          arrow
          placement="top"
          enterDelay={300}
          disableInteractive
        >
          <span className="live-inbox-toggle-wrap">
            <button
              type="button"
              className={`live-inbox-toggle ${a.online ? 'online' : 'offline'}${
                interactive ? '' : ' is-disabled'
              }`}
              onClick={onClick}
              disabled={!interactive || busy}
              aria-disabled={!interactive || busy}
            >
              {busy ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={a.online ? faToggleOn : faToggleOff} />
              )}
              <span>
                {a.online
                  ? t('live_inbox_online') || 'Online'
                  : t('live_inbox_offline') || 'Offline'}
              </span>
            </button>
          </span>
        </MuiTooltip>
      </li>
    );
  };

  const sorted = roster
    ? [...roster].sort((a, b) => {
        if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
        if (a.online !== b.online) return a.online ? -1 : 1;
        const an = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
        const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
        return an.localeCompare(bn);
      })
    : [];
  const selfAgent = sorted.find((a) => a.is_self);
  const otherAgents = sorted.filter((a) => !a.is_self);
  const modalOnline = sorted.filter((a) => a.online).length;

  return (
    <>
      <div className="agent-availability-controls">
        {isInitialLoading ? (
          <>
            <button type="button" className="agent-availability-toggle loading" disabled>
              <FontAwesomeIcon icon={faToggleOff} />
              <span>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            </button>
            <button type="button" className="agent-team-chip loading" disabled>
              <FontAwesomeIcon icon={faUsers} />
              <span>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`agent-availability-toggle ${online ? 'online' : 'offline'}`}
              onClick={handleToggle}
              disabled={toggling}
            >
              {toggling ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={online ? faToggleOn : faToggleOff} />
              )}
              <span>
                {online
                  ? t('live_inbox_online') || 'Online'
                  : t('live_inbox_offline') || 'Offline'}
              </span>
            </button>
            {showTeamChip && (
              <button
                type="button"
                className={`agent-team-chip ${onlineCount > 0 ? 'has-online' : 'all-offline'}`}
                onClick={() => setModalOpen(true)}
                title={t('bots_team_availability_tooltip') || 'Show team availability'}
              >
                <FontAwesomeIcon icon={faUsers} />
                <span>
                  {onlineCount}/{totalAgents}
                </span>
              </button>
            )}
          </>
        )}
      </div>

      {createPortal(
        <>
          {modalOpen && (
        <div className="agent-team-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="agent-team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agent-team-modal-header">
              <div className="live-inbox-availability-title-block">
                <span className="live-inbox-availability-title live-inbox-card-title">
                  <span>{t('bots_team_availability_title') || 'Team availability'}</span>
                  <InfoTooltip
                    text={
                      t('live_inbox_availability_tooltip') ||
                      'Live agents who are online can be requested by visitors directly from the chat.'
                    }
                  />
                </span>
                <span className="live-inbox-availability-desc">
                  {modalOnline > 0
                    ? t('live_inbox_availability_desc_some_online') ||
                      'At least one agent is online. Visitors can request a human agent.'
                    : t('live_inbox_availability_desc_all_offline') ||
                      'All agents are offline. Visitors cannot request a human agent.'}
                </span>
              </div>
              <span className="live-inbox-availability-count">
                {modalOnline}/{sorted.length} {t('live_inbox_online') || 'online'}
              </span>
              <button
                type="button"
                className="agent-team-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label={t('close') || 'Close'}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="agent-team-modal-body">
              {selfAgent && (
                <div className="live-inbox-agent-section">
                  <div className="live-inbox-agent-section-header">
                    {t('live_inbox_my_availability') || 'My availability'}
                  </div>
                  <ul className="live-inbox-agent-list">{renderRow(selfAgent)}</ul>
                </div>
              )}
              <div className="live-inbox-agent-section-divider" />
              <div className="live-inbox-agent-section">
                <div className="live-inbox-agent-section-header">
                  {t('live_inbox_other_agents') || 'Other agents'}
                </div>
                {otherAgents.length === 0 ? (
                  <div className="live-inbox-agent-empty-block">
                    <div className="live-inbox-agent-empty">
                      {t('live_inbox_no_other_agents') ||
                        'No other agents in this organization yet.'}
                    </div>
                    {canWrite() && (
                      <button
                        type="button"
                        className="live-inbox-invite-btn"
                        onClick={() => {
                          setModalOpen(false);
                          navigate('/team-members');
                        }}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                        <span>{t('live_inbox_invite_team_members') || 'Invite team members'}</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <ul
                    className={`live-inbox-agent-list${
                      otherAgents.length > 3 ? ' is-scrollable' : ''
                    }`}
                  >
                    {otherAgents.map(renderRow)}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <WarningModal
        open={!!pendingForce}
        onClose={() => setPendingForce(null)}
        title={t('force_offline_title') || 'Set teammate offline?'}
        cancelText={t('cancel') || 'Cancel'}
        confirmText={t('force_offline_confirm') || 'Set offline'}
        danger
        onConfirm={() => {
          if (!pendingForce) return;
          const { userId } = pendingForce;
          setPendingForce(null);
          handleForceOffline(userId);
        }}
      >
        {pendingForce && (
          <>
            {t('force_offline_body_prefix') || 'Are you sure you want to set'}{' '}
            <strong>{pendingForce.name}</strong>{' '}
            {t('force_offline_body_suffix') || 'offline?'}
            <br />
            <br />
            {t('force_offline_warning_prefix') || 'Note: only the teammate themselves can '}
            <strong>
              {t('force_offline_warning_bold') || 'set their status back to online'}
            </strong>
            {t('force_offline_warning_suffix') || ' from their own account.'}
          </>
        )}
      </WarningModal>
        </>,
        document.body
      )}
    </>
  );
});

export default AgentAvailability;
